import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  take
} from 'rxjs/operators';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { EnterpriseSearchService } from '@sabstravtech/obtservices/angular';
import { LocationDetails } from '@sabstravtech/obtservices/base';

import { ChatWidgetInputBase } from '../chat-widget-input-base';

export interface ChatChoice {
  title: string;
  value: string;
}

export interface ChatChoiceAnswer {
  title: string;
  value: string;
  email?: string;
  stationCode?: string;
  stationTitle?: string;
}

@Component({
  selector: 'app-chat-choice-input',
  standalone: false,
  templateUrl: './chat-choice-input.component.html',
  styleUrls: ['./chat-choice-input.component.scss']
})
export class ChatChoiceInputComponent extends ChatWidgetInputBase implements OnChanges {
  @Input() passengerName = '';
  @Input() choices: ChatChoice[] = [];
  @Input() savedValue: Record<string, unknown> | null = null;
  @Input() savedValueKey = '';
  @Input() fieldLabel = '';
  @Input() placeholder = '';
  @Input() required?: boolean;
  @Input() emailForValue?: string;
  @Input() emailLabel = 'Email';
  @Input() defaultEmail = '';
  @Input() stationForValue?: string;
  @Input() stationLabel = 'Station';
  @Input() infoForValue?: string;
  @Input() infoMessageStationName?: string;
  @Input() defaultStationName?: string;
  @Input() defaultStationCode?: string;
  @Input() showSkip = true;
  @Output() answer = new EventEmitter<ChatChoiceAnswer>();
  @Output() selectionChange = new EventEmitter<void>();
  @Output() skip = new EventEmitter<void>();

  selectedValue = '';
  emailValue = '';
  stationValue: LocationDetails | string | null = null;
  selectedStation: LocationDetails | null = null;
  chosenSummary: string | null = null;

  constructor(private searchService: EnterpriseSearchService) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.onlyLockedChanged(changes)) {
      return;
    }
    const savedValue = this.savedValue?.[this.savedValueKey];
    this.selectedValue = typeof savedValue === 'string' ? savedValue : '';
    this.emailValue = this.defaultEmail;
    this.stationValue = null;
    this.selectedStation = null;
    if (this.defaultStationName && this.defaultStationCode) {
      this.stationValue = this.defaultStationName;
      this.selectedStation = {
        name: this.defaultStationName,
        railstation: { stationCode: this.defaultStationCode }
      } as LocationDetails;
    } else if (this.defaultStationName) {
      this.searchService
        .getStationLocations(this.defaultStationName, undefined, true)
        .pipe(
          take(1),
          catchError(() => of([]))
        )
        .subscribe((stations) => {
          const station = (stations ?? []).find(
            (candidate) =>
              candidate.name?.toLowerCase() === this.defaultStationName?.toLowerCase() &&
              !!candidate.railstation?.stationCode &&
              !!(candidate.railstation as { hasTod?: boolean } | undefined)?.hasTod
          );
          if (station) {
            this.stationValue = station;
            this.selectedStation = station;
          }
        });
    }
    this.chosenSummary = null;
  }

  get isMandatory(): boolean {
    return this.required === true;
  }

  get selectedChoiceText(): string {
    const selectedChoice = this.choices.find((choice) => choice.value === this.selectedValue);
    return `${this.selectedValue} ${selectedChoice?.title ?? ''}`.toLowerCase();
  }

  get isValid(): boolean {
    if (!this.selectedValue && this.isMandatory) {
      return false;
    }
    if (this.emailForValue && this.selectedChoiceText.includes(this.emailForValue)) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.emailValue.trim());
    }
    if (this.stationForValue && this.selectedChoiceText.includes(this.stationForValue)) {
      return (
        typeof this.stationValue !== 'string' &&
        !!this.selectedStation &&
        this.selectedStation === this.stationValue &&
        !!this.selectedStation.railstation?.stationCode &&
        !!(this.selectedStation.railstation as { hasTod?: boolean }).hasTod
      );
    }
    return true;
  }

  formatterStation = (station: LocationDetails | string | null | undefined): string => {
    if (typeof station === 'string') {
      return station;
    }
    return typeof station?.name === 'string' ? station.name : '';
  };

  railLocations = (text$: Observable<string>): Observable<LocationDetails[]> =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) =>
        term.length <= 2
          ? of([])
          : this.searchService.getStationLocations(term, undefined, true).pipe(
              map((stations) =>
                (stations ?? [])
                  .filter(
                    (station) => !!(station.railstation as { hasTod?: boolean } | undefined)?.hasTod
                  )
                  .slice(0, 20)
              ),
              catchError(() => of([]))
            )
      )
    );

  onStationSelected(event: NgbTypeaheadSelectItemEvent<LocationDetails>): void {
    this.selectedStation = event.item;
    this.stationValue = event.item;
  }

  onStationValueChanged(value: LocationDetails | string | null): void {
    this.stationValue = value;
    if (typeof value !== 'object' || value === null) {
      this.selectedStation = null;
    }
  }

  onContinue(): void {
    if (this.locked || !this.isValid) {
      return;
    }
    if (this.selectedValue) {
      const choice = this.choices.find((item) => item.value === this.selectedValue);
      const title = choice?.title ?? this.selectedValue;
      const email = this.emailValue.trim() || undefined;
      const stationTitle = this.selectedStation?.name;
      const selectedChoiceText = this.selectedChoiceText;

      const dependentSummary =
        this.emailForValue && selectedChoiceText.includes(this.emailForValue)
          ? email
          : this.stationForValue && selectedChoiceText.includes(this.stationForValue)
            ? stationTitle
            : undefined;

      this.chosenSummary = [title, dependentSummary]
        .filter((value): value is string => !!value)
        .join(' - ');

      this.answer.emit({
        value: this.selectedValue,
        title,
        email,
        stationCode: this.selectedStation?.railstation?.stationCode ?? undefined,
        stationTitle
      });
      return;
    }
    this.chosenSummary = 'None';
    this.skip.emit();
  }

  onSelectionChanged(): void {
    this.selectionChange.emit();
  }

  onSkip(): void {
    if (this.locked) {
      return;
    }
    this.chosenSummary = 'Skipped';
    this.skip.emit();
  }
}
